import React, { useCallback, useEffect, useMemo } from 'react';

import useFetchData from '@/hooks/useFetchData';
import { useTranslate } from '@/hooks/useTranslate';
import { fetchViralTemplatePage } from '@/services/viralTemplate';
import { ViralTemplateExposeFromEnum } from '@/utils/eventlog/eventNewConfig';

import { useViralTemplateFormModal } from '../../../../hooks/useViralTemplateFormModal';
import { VIRAL_LIST_CATEGORY_ADS } from '../../../../hooks/useViralTemplateRouteState';
import type { TplTemplateVO } from '../../../template-ui/types';
import CategorySectionShell from './CategorySectionShell';
import { shouldShowPreviewViewMore } from './showViewMore';
import { useColumns } from './useColumns';

export default function MetaTopAdsPreviewSection({
  onViewMore,
}: {
  onViewMore: () => void;
}) {
  const { t } = useTranslate();
  const { openFormModal, loadingDetail } = useViralTemplateFormModal(
    ViralTemplateExposeFromEnum.TEMPLATE,
  );
  const { ref, columns } = useColumns();

  const pageSize = columns * 2;

  const fetchFn = useCallback(
    (pageNum: number, pageSizeArg: number) =>
      fetchViralTemplatePage({
        category: VIRAL_LIST_CATEGORY_ADS,
        pageNum,
        pageSize: pageSizeArg,
      }),
    [],
  );

  const {
    data: items,
    loading,
    rawResponse,
    total,
    refresh,
  } = useFetchData<TplTemplateVO>({
    fetchFn,
    pageSize,
  });

  useEffect(() => {
    void refresh();
  }, [pageSize, refresh]);

  const showViewMore = useMemo(() => {
    const totalCount =
      typeof rawResponse?.totalCount === 'number' ? rawResponse.totalCount : total;
    return shouldShowPreviewViewMore({
      loading,
      success: rawResponse?.code === 200 && rawResponse?.success === true,
      itemCount: items.length,
      pageSize,
      totalCount,
      hasMore: Boolean(rawResponse?.hasMore),
    });
  }, [items.length, loading, rawResponse, total, pageSize]);

  const header = useMemo(
    () => (
      <div className="flex flex-col gap-[4px] w-full">
        <span className="font-[600] text-[16px] leading-[22px] text-[#111111]">
          {t('viralTemplate.metaTopAds.title')}
        </span>
        <span className="text-[13px] text-[#11111199] leading-[18px]">
          {t('viralTemplate.metaTopAds.desc')}
        </span>
      </div>
    ),
    [t],
  );

  return (
    <>
      <CategorySectionShell
        header={header}
        gridAnchorRef={ref}
        items={items}
        loading={loading}
        from={ViralTemplateExposeFromEnum.TEMPLATE}
        columns={columns}
        showViewMore={showViewMore}
        onViewMore={onViewMore}
        onReplicate={(tpl) => void openFormModal(tpl)}
      />
      {loadingDetail ? (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/25">
          <div className="h-[20px] w-[20px] animate-spin rounded-full border-2 border-[#4931FA]/40 border-t-[#4931FA]" />
        </div>
      ) : null}
    </>
  );
}
